/**
 * Alertas. Un único servicio las crea, escala y cierra (reglas en docs/REGLAS.md y reglas-alerta.ts).
 *
 *   GET  /alertas?estado=&tipo=&nivel=&viajeId=&vehiculoIdMicrotrack=&desde=&hasta=&pagina=&tamanio=  → AlertasRespuesta
 *   GET  /alertas/:id                                                                               → Alerta
 *   POST /alertas/:id/resolucion      body ResolverAlertaBody   (DESPACHO, SUPERVISION, ADMIN)      → Alerta
 *   POST /alertas/:id/justificacion   body JustificarAlertaBody (DESPACHO, SUPERVISION, ADMIN)      → Alerta
 */
import type { Coordenadas, FechaIso, Pagina } from './comunes';
import type { NivelAlerta } from './reglas-alerta';

export const TIPOS_ALERTA = [
  'PARADA_NO_AUTORIZADA',
  'PARADA_NO_PLANIFICADA',
  'PARADA_OMITIDA',
  'PARADA_EXCEDIDA',
  'DESVIO_RUTA',
  'CAMBIO_RUTA',
  'EXCESO_VELOCIDAD',
  'RALENTI_EXCESIVO',
  'INCONSISTENCIA_MARCACION',
  'SIN_REPORTE',
  'INICIO_DEMORADO',
  'RETRASO_ETA',
  'RECURSO_INHABILITADO',
  'VIAJE_SIN_RUTA',
] as const;
export type TipoAlerta = (typeof TIPOS_ALERTA)[number];

export const ESTADOS_ALERTA = ['ABIERTA', 'RESUELTA', 'JUSTIFICADA'] as const;
export type EstadoAlerta = (typeof ESTADOS_ALERTA)[number];

/**
 * Nivel con el que nace cada tipo si ninguna regla dice otra cosa. Es el primer escalón de la regla GENERAL inicial
 * (esquema inicial). Reemplaza al viejo SEVERIDAD_POR_TIPO.
 */
export const NIVEL_INICIAL_POR_TIPO: Record<TipoAlerta, NivelAlerta> = {
  PARADA_NO_AUTORIZADA: 'ADVERTENCIA',
  PARADA_NO_PLANIFICADA: 'INFORMATIVA',
  PARADA_OMITIDA: 'ADVERTENCIA',
  PARADA_EXCEDIDA: 'ADVERTENCIA',
  DESVIO_RUTA: 'CRITICA',
  CAMBIO_RUTA: 'INFORMATIVA',
  EXCESO_VELOCIDAD: 'ADVERTENCIA',
  RALENTI_EXCESIVO: 'ADVERTENCIA',
  INCONSISTENCIA_MARCACION: 'ADVERTENCIA',
  SIN_REPORTE: 'CRITICA',
  INICIO_DEMORADO: 'ADVERTENCIA',
  RETRASO_ETA: 'ADVERTENCIA',
  RECURSO_INHABILITADO: 'CRITICA',
  VIAJE_SIN_RUTA: 'ADVERTENCIA',
};

/** Título en español por tipo, para la UI y los mensajes. */
export const TITULO_POR_TIPO: Record<TipoAlerta, string> = {
  PARADA_NO_AUTORIZADA: 'Detención no autorizada',
  PARADA_NO_PLANIFICADA: 'Parada no planificada declarada',
  PARADA_OMITIDA: 'Parada omitida',
  PARADA_EXCEDIDA: 'Parada excedida',
  DESVIO_RUTA: 'Desvío de recorrido',
  CAMBIO_RUTA: 'Cambio de ruta',
  EXCESO_VELOCIDAD: 'Exceso de velocidad',
  RALENTI_EXCESIVO: 'Ralentí excesivo',
  INCONSISTENCIA_MARCACION: 'Aviso inconsistente con el GPS',
  SIN_REPORTE: 'Unidad sin reportar',
  INICIO_DEMORADO: 'Inicio demorado',
  RETRASO_ETA: 'Demora en arribo',
  RECURSO_INHABILITADO: 'Recurso inhabilitado',
  VIAJE_SIN_RUTA: 'Viaje sin ruta',
};

export interface Alerta {
  id: number;
  tipo: TipoAlerta;
  /** Nivel vigente: puede escalar mientras la alerta está abierta. */
  nivel: NivelAlerta;
  /**
   * Nivel con el que se abrió la alerta (no cambia al escalar). Null si no se conoce: toda alerta que abre el TMS lo
   * trae, pero la columna admite null.
   */
  nivelInicial: NivelAlerta | null;
  /** Regla que la generó; null si se creó con el nivel inicial del tipo (sin regla). */
  reglaId: number | null;
  titulo: string;
  estado: EstadoAlerta;
  viajeId: number | null;
  viajeIdMara: string | null;
  vehiculoIdMicrotrack: string | null;
  /** Patente (dominio de Mara) de la unidad. */
  patente: string | null;
  choferIdMara: string | null;
  choferNombre: string | null;
  /** Situación dentro del tipo (ej. "parada:15"). */
  clave: string;
  ubicacion: Coordenadas | null;
  /** Referencia legible del lugar: la parada de la clave o la parada del viaje más cercana (≤ 5 km), o null. */
  ubicacionNombre: string | null;
  /** Cuándo empezó la condición. */
  inicio: FechaIso;
  /** Cuándo cesó la condición; null mientras persiste. */
  fin: FechaIso | null;
  cerradaAutomaticamente: boolean;
  cambiadoPor: { id: number; nombre: string } | null;
  cambiadoEn: FechaIso | null;
  motivo: string | null;
  /**
   * Datos propios del tipo. Ejemplos:
   * EXCESO_VELOCIDAD { velocidadKmh, maximaKmh }; DESVIO_RUTA { distanciaM, bufferM, rutaId };
   * CAMBIO_RUTA { rutaAnteriorId, rutaNuevaId }; PARADA_NO_AUTORIZADA { detencionId, minutos };
   * PARADA_EXCEDIDA { paradaId, permanenciaMin, duracionMaxEsperadaMin }; PARADA_OMITIDA { paradaId, orden };
   * PARADA_NO_PLANIFICADA { paradaNoPlanificadaId, motivo, observacion }; SIN_REPORTE { ultimoReporte, minutos };
   * INCONSISTENCIA_MARCACION { marcacion, distanciaM, antiguedadReporteMin };
   * RECURSO_INHABILITADO { recurso: 'CHOFER'|'VEHICULO', idMara, detalle }; VIAJE_SIN_RUTA { troncalId, troncalIdMara }.
   */
  detalle: Record<string, unknown>;
  creadoEn: FechaIso;
}

/** GET /alertas: filtros por query string. */
export interface FiltroAlertas {
  estado?: EstadoAlerta | EstadoAlerta[];
  tipo?: TipoAlerta | TipoAlerta[];
  nivel?: NivelAlerta | NivelAlerta[];
  viajeId?: number;
  vehiculoIdMicrotrack?: string;
  desde?: FechaIso;
  hasta?: FechaIso;
  pagina?: number;
  tamanio?: number;
}

/** POST /alertas/:id/resolucion (DESPACHO, SUPERVISION, ADMIN). */
export interface ResolverAlertaBody {
  motivo?: string | null;
}

/** POST /alertas/:id/justificacion. Motivo obligatorio (mín. 5 caracteres). */
export interface JustificarAlertaBody {
  motivo: string;
}

/** Respuesta de GET /alertas, ordenada por inicio descendente. */
export type AlertasRespuesta = Pagina<Alerta>;
